<script>
import baseMixin from '@/mixins/baseMixin'
import idleMixin from '@/mixins/idleMixin'
import bookingFiltersMixin from '@/mixins/bookingFiltersMixin'
import { mapActions, mapGetters } from 'vuex'
import SortOrder from '@/shared/constants/core/SortOrder'
import BookingListTabKey from '@/shared/constants/booking/BookingListTabKey'
import ExportMode from '@/shared/constants/booking/ExportMode'
import CandidateZoom from '@/components/candidate/candidate-zoom.vue'
import MobileNavDrawer from '@/components/layout/mobile-nav-drawer.vue'
import TopFilterBar from '@/components/booking/bookingList/top-filter-bar.vue'
import BookingHeader from '@/components/booking/bookingList/booking-header.vue'
import BookingRow from '@/components/booking/bookingList/booking-row.vue'
import BookingListTab from '@/components/booking/bookingList/booking-list-tab.vue'
import NoBookingFound from '@/components/booking/bookingList/no-booking-found.vue'
import BookingRowSkeleton from '@/components/booking/bookingList/booking-row-skeleton.vue'
import ExportBookings from '@/components/booking/bookingList/export-bookings.vue'
import BookingSidePanel from '@/components/booking/bookingList/side-panel/booking-side-panel.vue'
import NoClientsSelected from '@/components/booking/bookingList/no-client-selected.vue'
import NoSearchResultFound from '@/components/booking/bookingList/no-search-result-found.vue'
import EmptyBookingsFound from '@/components/booking/bookingList/empty-bookings-found.vue'
import { getTodaysDateString } from '@/helpers/date-helpers'

export default {
  name: 'BookingListContainer',
  components: {
    TopFilterBar,
    BookingHeader,
    BookingRow,
    BookingListTab,
    NoBookingFound,
    BookingRowSkeleton,
    CandidateZoom,
    ExportBookings,
    BookingSidePanel,
    NoClientsSelected,
    NoSearchResultFound,
    EmptyBookingsFound,
    MobileNavDrawer,
  },
  mixins: [baseMixin, idleMixin, bookingFiltersMixin],
  data: () => ({
    sortKey: 'date',
    sortOrder: SortOrder.ascending,
    approvalCount: 0,
    loading: false,
    pageNumber: 1,
    isFetchingMore: false,
    showCandidateModal: false,
    showExportModal: false,
    showSidePanel: false,
    showApprovalSidePanel: false,
    bookingDetails: null,
    exportMode: ExportMode.All,
    selectedCandidate: '',
    isBlockBookingFlag: false,
    headers: [],
    selectedRows: [],
    showNotes: false,
    notesLocked: false,
    currentTabName: BookingListTabKey.upcoming,
    activeTab: 1,
    // Filter option data (loaded from API)
    educators: [],
    classifications: [],
    certifications: [],
    replacing: [],
    grades: [],
  }),

  computed: {
    ...mapGetters({
      bookings: 'bookings/bookingList',
      totalPages: 'bookings/bookingListTotalPages',
      permissions: 'auth/permissions',
      approvalRequiredCount: 'bookings/approvalRequiredCount',
      batchFinalizedAt: 'bookingApprovals/batchFinalizedAt',
      selectedClientIds: 'client/getSelectedClients',
      clientList: 'auth/getAllClients',
      locations: 'auth/getAllLocations',
    }),
    isAscendingSortOrder() {
      return this.sortOrder === SortOrder.ascending
    },
    isDescendingSortOrder() {
      return this.sortOrder === SortOrder.descending
    },
    adjustedHeaders() {
      return this.headers.filter((h) => {
        if (
          this.activeTab !== 0 &&
          !this.isActiveTab(BookingListTabKey.approvalRequired)
        ) {
          if (['days'].includes(h.key)) return false
        }
        if (
          this.isActiveTab(
            BookingListTabKey.upcoming,
            BookingListTabKey.past,
            BookingListTabKey.all
          )
        ) {
          if (['client'].includes(h.key)) return false
        }
        if (
          this.isActiveTab(
            BookingListTabKey.approvalRequired,
            BookingListTabKey.cancelled
          )
        ) {
          if (['status'].includes(h.key)) return false
        }
        return true
      })
    },

    filteredBookings() {
      return this.bookings || []
    },

    allSelected() {
      return (
        this.selectedRows.length === this.filteredBookings.length &&
        this.filteredBookings.length > 0
      )
    },
    selectedBookingData() {
      return this.filteredBookings.filter((b) =>
        this.selectedRows.includes(b.id)
      )
    },
    exportFilters() {
      const to24HourDateTime = (dateStr, timeStr, fallback = '00:00') => {
        if (!dateStr) return null

        const [y, m, d] = dateStr.split('-').map(Number)
        const [hh, mm] = (timeStr ?? fallback).split(':').map(Number)

        // Pad numbers to 2 digits
        const pad = (n) => String(n).padStart(2, '0')

        return `${y}-${pad(m)}-${pad(d)} ${pad(hh)}:${pad(mm)}:00`
      }

      const startTime = this.storeStartTime
      const endTime = this.storeEndTime
      const hasAnyTime = !!(startTime || endTime)

      const sendStart = hasAnyTime
        ? to24HourDateTime(this.filterStartDate, startTime, '00:00')
        : this.filterStartDate

      const sendEndBase = this.filterEndDate ?? this.filterStartDate
      const sendEnd = hasAnyTime
        ? to24HourDateTime(sendEndBase, endTime ?? startTime, '23:59')
        : sendEndBase

      const locationsArray =
        this.filterLocations && this.filterLocations.length > 0
          ? this.filterLocations
          : this.locationIds

      return {
        tabType: this.currentTabName,
        isBlockBooking: this.isBlockBookingFlag,
        startDate: sendStart,
        endDate: sendEnd,
        dateFilterCondition: this.filterCondition,
        clientids: this.selectedClientIds,
        status: this.filterStatuses,
        educators: this.filterEducators,
        classifications: this.filterClassifications,
        certifications: this.filterCertifications,
        grades: this.filterGrades,
        replacings: this.filterReplacing,
        locations: locationsArray,
        sortColumn: this.sortKey,
        isDescending: this.isDescendingSortOrder,
      }
    },
    locationIds() {
      return this.locations.map((location) => location.locationId)
    },
  },

  watch: {
    isBlockBookingFlag: {
      immediate: true,
      handler() {
        this.headers = this.getTranslatedHeaders()
      },
    },
    batchFinalizedAt(newVal) {
      if (newVal) {
        this.getBookings()
      }
    },
  },

  created() {
    this.headers = this.getTranslatedHeaders()
  },

  async mounted() {
    this.getFilterData(this.selectedClientIds, this.locationIds)
    await this.getBookings()
    window.addEventListener('scroll', this.handlePageScroll)
  },

  beforeDestroy() {
    window.removeEventListener('scroll', this.handlePageScroll)
  },

  methods: {
    ...mapActions({
      getCertifications: 'locations/getCertifications',
      getReplacing: 'locations/getReplacing',
      getClassifications: 'client/getClassifications',
      getGrades: 'client/getGrades',
      getCandidates: 'candidate/getCandidates',
      fetchBookings: 'bookings/getBookings',
    }),
    /**
     * Indicates if any of the provided tabKeys match the currently open (active) tab
     * @param {...string} tabKeys - One or more tab keys to check
     * @returns {boolean} True if any of the provided tab keys match currentTabName
     */
    isActiveTab(...tabKeys) {
      return tabKeys.includes(this.currentTabName)
    },
    async getBookings(isPagination = false) {
      if (!isPagination) {
        this.pageNumber = 1
        this.loading = true
      } else {
        this.isFetchingMore = true
      }

      const locationsArray =
        this.filterLocations && this.filterLocations.length > 0
          ? this.filterLocations
          : this.locationIds

      const sTime = this.storeStartTime ? `${this.storeStartTime}:00` : ''
      const eTime = this.storeEndTime ? `${this.storeEndTime}:00` : ''

      const setPastTabDefaultDates =
        this.isActiveTab(BookingListTabKey.past) && !this.filterEndDate

      const payload = {
        tabType: this.currentTabName,
        IsBlockBooking: this.isBlockBookingFlag,
        search: this.filterSearchQuery || '',
        clientids: this.selectedClientIds,
        status: this.filterStatuses,
        educators: this.filterEducators,
        locations: locationsArray,
        classifications: this.filterClassifications,
        certifications: this.filterCertifications,
        grades: this.filterGrades,
        replacings: this.filterReplacing,
        pageSize: 20,
        pageNumber: this.pageNumber,
        sortColumn: this.sortKey,
        isDescending: this.isDescendingSortOrder,
        ...(!this.isActiveTab(BookingListTabKey.approvalRequired)
          ? {
              startDate: !setPastTabDefaultDates ? this.filterStartDate : null,
              endDate: !setPastTabDefaultDates
                ? this.filterEndDate
                : getTodaysDateString(),
              startTime: sTime,
              endTime: eTime,
            }
          : {}),
      }

      await this.fetchBookings(payload)

      if (!isPagination) {
        this.selectedRows = []
      }

      this.approvalCount = this.approvalRequiredCount

      this.loading = false
      this.isFetchingMore = false
    },
    async getFilterData(clientIds, locationIds) {
      if (!clientIds?.length || !locationIds?.length) return

      const [
        candidates,
        classifications,
        certifications,
        replacing,
        grades,
      ] = await Promise.all([
        this.getCandidates(clientIds),
        this.getClassifications(clientIds),
        this.getCertifications(locationIds),
        this.getReplacing(locationIds),
        this.getGrades(clientIds),
      ])

      this.educators = candidates.data || []
      this.classifications = classifications.data || []
      this.certifications = certifications.data || []
      this.replacing = replacing.data || []
      this.grades = grades.data || []
    },

    sortData(key) {
      const isSortingSameColumn = this.sortKey === key

      if (isSortingSameColumn) {
        this.toggleSortOrder()
      } else {
        this.sortKey = key
        this.sortOrder = SortOrder.ascending
      }

      this.getBookings()
    },

    toggleSortOrder() {
      this.sortOrder = this.isAscendingSortOrder
        ? SortOrder.descending
        : SortOrder.ascending
    },

    toggleRowSelection(id) {
      const idx = this.selectedRows.indexOf(id)
      if (idx > -1) {
        this.selectedRows.splice(idx, 1)
      } else {
        this.selectedRows.push(id)
      }
    },

    toggleSelectAll() {
      this.selectedRows = this.allSelected
        ? []
        : this.filteredBookings.map((b) => b.id)
    },
    handleTableMouseEnter() {
      if (!this.notesLocked) this.showNotes = true
    },
    handleTableMouseLeave() {
      if (!this.notesLocked) this.showNotes = false
    },
    updateAndLockShowNotes(val) {
      this.showNotes = val
      this.notesLocked = val
    },
    handleTabChange(tabName) {
      this.currentTabName = tabName
      if (
        this.isActiveTab(
          BookingListTabKey.approvalRequired,
          BookingListTabKey.cancelled
        )
      ) {
        this.$refs.topFilterBar.toggleDayByDayBlock(0)
      } else {
        this.getBookings()
      }
    },
    handleSearch(searchText) {
      this.setSearchQuery(searchText)
      this.getBookings()
    },
    handleResetSearch() {
      this.setSearchQuery('')
      if (
        this.$refs.topFilterBar &&
        this.$refs.topFilterBar.$refs.searchBookings.clearSearchBarAndClose
      ) {
        this.$refs.topFilterBar.$refs.searchBookings.clearSearchBarAndClose()
      }
      this.getBookings()
    },
    handleResetFilters() {
      this.resetAllFilters()
      this.getBookings()
    },
    handlePageScroll() {
      const scrollTop = window.scrollY || document.documentElement.scrollTop
      const windowHeight = window.innerHeight
      const documentHeight = document.documentElement.offsetHeight

      const nearBottom = scrollTop + windowHeight >= documentHeight - 100

      if (
        nearBottom &&
        !this.loading &&
        !this.isFetchingMore &&
        this.pageNumber < this.totalPages
      ) {
        this.pageNumber++
        this.getBookings(true)
      }
    },
    showCandidateDialog(candidateId) {
      this.selectedCandidate = candidateId
      this.showCandidateModal = true
    },
    closeCandidateDialog() {
      this.selectedCandidate = null
      this.showCandidateModal = false
    },
    handleDateFilter({ startDate, endDate }) {
      this.setDateFilter({ startDate, endDate })
      this.getBookings()
    },
    openExportAll() {
      this.exportMode = ExportMode.All
      this.showExportModal = true
    },
    openExportSelected() {
      this.exportMode = ExportMode.Selected
      this.showExportModal = true
    },
    handleClientFilter(newIds) {
      this.getBookings()
    },
    showAllClients() {
      this.setClientList(this.clientList.map((c) => c.clientId))
      this.getBookings()
    },
    openSidePanel(bookingDetails) {
      this.bookingDetails = bookingDetails
      this.showSidePanel = true
    },
    getTranslatedHeaders() {
      return [
        {
          key: 'date',
          label: this.$t('bookingList.headerName.date'),
          width: this.isBlockBookingFlag ? '210px' : '150px',
        },
        {
          key: 'days',
          label: this.$t('bookingList.headerName.days'),
          width: '150px',
        },
        {
          key: 'educator',
          label: this.$t('bookingList.headerName.educator'),
          width: this.isBlockBookingFlag ? '150px' : '250px',
        },
        {
          key: 'time',
          label: this.$t('bookingList.headerName.time'),
          width: '180px',
        },
        {
          key: 'status',
          label: this.$t('bookingList.headerName.status'),
          width: '125px',
        },
        {
          key: 'classification',
          label: this.$t('bookingList.headerName.classification'),
          width: '150px',
        },
        {
          key: 'room',
          label: this.$t('bookingList.headerName.room'),
          width: '100px',
        },
        {
          key: 'replacing',
          label: this.$t('bookingList.headerName.replacing'),
          width: '150px',
        },
        {
          key: 'grade',
          label: this.$t('bookingList.headerName.grade'),
          width: 'auto',
        },
        {
          key: 'certifications',
          label: this.$t('bookingList.headerName.certification'),
          width: 'auto',
        },
        {
          key: 'client',
          label: this.$t('bookingList.headerName.client'),
          width: 'auto',
        },
      ]
    },
    handleFilterChange(filters) {
      this.applyFilters(filters)
      this.getBookings()
    },
    onViewModeChange(e) {
      this.activeTab = e
      this.isBlockBookingFlag = e === 0
      this.getBookings()
    },
  },
}
</script>

<template>
  <div>
    <div
      class="d-flex px-3"
      style="align-items: flex-start; justify-content: space-between"
    >
      <div
        class="d-flex pt-2"
        style="align-items: center; justify-content: center"
      >
        <MobileNavDrawer />
        <v-icon class="me-3" :class="[isMobileViewPort ? 'ml-4' : '']" size="20"
          >mdi-calendar-month</v-icon
        >
        <h5 class="page-title">{{ $t('bookingList.pageTitle') }}</h5>
      </div>
      <div>
        <TopFilterBar
          ref="topFilterBar"
          :model-value="filterSearchQuery"
          :selected-count="selectedRows.length"
          :current-tab-name="currentTabName"
          :educators="educators"
          :classifications="classifications"
          :certifications="certifications"
          :grades="grades"
          :replacing="replacing"
          @filter-change="handleFilterChange"
          @active-tab="onViewModeChange"
          @search="handleSearch"
          @date-filter="handleDateFilter"
          @export="openExportAll"
          @export-selected="openExportSelected"
          @update:selectedClientIds="handleClientFilter"
        />
      </div>
    </div>

    <div class="w-full">
      <div class="table-scroll-wrapper">
        <BookingListTab
          ref="bookingTabs"
          :approval-count="approvalCount"
          @set-tab="handleTabChange"
        />
        <div
          class="table-container"
          @mouseenter="handleTableMouseEnter"
          @mouseleave="handleTableMouseLeave"
        >
          <table>
            <BookingHeader
              :headers="adjustedHeaders"
              :sort-key="sortKey"
              :sort-order="sortOrder"
              :all-selected="allSelected"
              :show-notes="showNotes"
              :current-tab-name="currentTabName"
              :active-tab="activeTab"
              @sort="sortData"
              @toggle-all="toggleSelectAll"
            />

            <!-- when there are results -->
            <tbody v-if="(selectedClientIds || []).length === 0" class="h-full">
              <tr style="height: 100%">
                <td
                  :colspan="headers.length + 1"
                  style="height: 100%; padding: 0"
                >
                  <div class="no-booking-wrapper">
                    <NoClientsSelected @reset="showAllClients" />
                  </div>
                </td>
              </tr>
            </tbody>

            <tbody v-else-if="loading && pageNumber === 1">
              <BookingRowSkeleton
                v-for="i in 20"
                :key="'skeleton-' + i"
                :colspan="adjustedHeaders.length + 3"
              />
            </tbody>

            <tbody v-else-if="filteredBookings.length">
              <BookingRow
                v-for="booking in filteredBookings"
                :key="`booking-row-${booking.id}`"
                :booking="booking"
                :selected="selectedRows.includes(booking.id)"
                :show-notes="showNotes"
                :current-tab-name="currentTabName"
                :active-tab="activeTab"
                @toggle-select="toggleRowSelection(booking.id)"
                @viewCandidate="showCandidateDialog"
                @sidePanel="openSidePanel"
                @update-show-notes="updateAndLockShowNotes"
                @reloadBookingList="getBookings"
              />
              <!-- Skeleton only for fetching next page -->
              <template v-if="isFetchingMore">
                <BookingRowSkeleton
                  v-for="i in 5"
                  :key="'loading-more-' + i"
                  :colspan="adjustedHeaders.length + 3"
                />
              </template>
            </tbody>

            <!-- when no results -->
            <tbody v-else class="h-full">
              <tr style="height: 100%">
                <td
                  :colspan="headers.length + 1"
                  style="height: 100%; padding: 0"
                >
                  <div class="no-booking-wrapper">
                    <NoSearchResultFound
                      v-if="filterSearchQuery && filterSearchQuery.length > 0"
                      @resetSearch="handleResetSearch"
                    />
                    <NoBookingFound
                      v-else-if="
                        isAnyFilterActive &&
                          !filterSearchQuery &&
                          (selectedClientIds || []).length > 0
                      "
                      @resetFilters="handleResetFilters"
                    />
                    <EmptyBookingsFound
                      v-else
                      :current-tab-name="currentTabName"
                    />
                  </div>
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>
    </div>

    <v-dialog
      v-if="showCandidateModal"
      v-model="showCandidateModal"
      width="720px"
      content-class="rounded-xl"
      scrollable
    >
      <CandidateZoom
        :id="selectedCandidate"
        @closeModal="closeCandidateDialog"
      />
    </v-dialog>

    <v-dialog
      v-if="showExportModal"
      v-model="showExportModal"
      width="600px"
      height="auto"
      content-class="rounded-xl"
    >
      <ExportBookings
        :export-mode="exportMode"
        :selected-count="selectedRows.length"
        :selected-bookings="selectedBookingData"
        :filters="exportFilters"
        @close="showExportModal = false"
        @unselected-rows="selectedRows = []"
      />
    </v-dialog>

    <BookingSidePanel v-model="showSidePanel" :booking="bookingDetails" />
  </div>
</template>

<style lang="scss" scoped>
@import '@/styles/_colors.scss';
@import '@/styles/bookingList/booking-list.scss';
</style>
